import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, RefreshCw, Save, X, Power, PowerOff } from 'lucide-react';
import { installmentRateService, InstallmentRate, CreateInstallmentRateRequest } from '../services/installmentRateService';
import { DefaultLoader } from '../components';

const InstallmentRates: React.FC = () => {
  const [rates, setRates] = useState<InstallmentRate[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingRate, setEditingRate] = useState<InstallmentRate | null>(null);
  const [formData, setFormData] = useState<CreateInstallmentRateRequest>({
    installments: 2,
    rate: 0,
    isActive: true,
  });

  useEffect(() => {
    loadRates();
  }, []);

  const loadRates = async () => {
    try {
      setLoading(true);
      const data = await installmentRateService.getAll();
      setRates(data);
    } catch (error) {
      console.error('Erro ao carregar taxas:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingRate) {
        await installmentRateService.update(editingRate.id, formData);
      } else {
        await installmentRateService.create(formData);
      }
      handleCancelForm();
      loadRates();
    } catch (error) {
      console.error('Erro ao salvar taxa:', error);
    }
  };

  const handleEdit = (rate: InstallmentRate) => {
    setEditingRate(rate);
    setFormData({
      installments: rate.installments,
      rate: rate.rate,
      isActive: rate.isActive,
    });
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja excluir esta taxa?')) return;
    try {
      await installmentRateService.delete(id);
      loadRates();
    } catch (error) {
      console.error('Erro ao excluir taxa:', error);
    }
  };

  const handleToggleActive = async (rate: InstallmentRate) => {
    try {
      await installmentRateService.update(rate.id, { isActive: !rate.isActive });
      loadRates();
    } catch (error) {
      console.error('Erro ao alterar status:', error);
    }
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingRate(null);
    setFormData({ installments: 2, rate: 0, isActive: true });
  };

  if (loading) {
    return <DefaultLoader title="Taxas de Parcelamento" messages={['Carregando taxas...', 'Processando dados...']} />;
  }

  return (
    <div>
      <div className="d-flex justify-between align-center mb-3">
        <div>
          <h2 className="mb-1">Taxas de Parcelamento</h2>
          <p className="mb-0" style={{ color: 'var(--color-dimgray)' }}>
            Gerencie as taxas de juros por número de parcelas
          </p>
        </div>
        <div className="d-flex gap-2">
          <button onClick={loadRates} className="btn btn-secondary">
            <RefreshCw size={16} />
            Atualizar
          </button>
          <button onClick={() => setShowForm(true)} className="btn btn-primary">
            <Plus size={16} />
            Nova Taxa
          </button>
        </div>
      </div>

      {showForm && (
        <div className="card mb-3">
          <h3 className="mb-3">
            {editingRate ? 'Editar Taxa' : 'Nova Taxa'}
          </h3>
          <form onSubmit={handleSubmit}>
            <div className="grid" style={{ gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
              <div className="form-group">
                <label className="form-label">Parcelas *</label>
                <input
                  type="number"
                  min="1"
                  max="12"
                  className="form-input"
                  value={formData.installments}
                  onChange={(e) => setFormData({ ...formData, installments: parseInt(e.target.value) })}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Taxa (%) *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="99.99"
                  className="form-input"
                  value={formData.rate}
                  onChange={(e) => setFormData({ ...formData, rate: parseFloat(e.target.value) })}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Ativo</label>
                <select
                  className="form-select"
                  value={formData.isActive ? 'true' : 'false'}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.value === 'true' })}
                >
                  <option value="true">Sim</option>
                  <option value="false">Não</option>
                </select>
              </div>
            </div>
            <div className="d-flex gap-2 mt-3">
              <button type="submit" className="btn btn-primary">
                <Save size={16} />
                {editingRate ? 'Atualizar' : 'Criar'}
              </button>
              <button type="button" onClick={handleCancelForm} className="btn btn-secondary">
                <X size={16} />
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="card">
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Parcelas</th>
                <th>Taxa (%)</th>
                <th>Status</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {rates.length === 0 ? (
                <tr>
                  <td colSpan={4} style={{ textAlign: 'center', padding: '24px' }}>
                    Nenhuma taxa cadastrada
                  </td>
                </tr>
              ) : (
                rates.map((rate) => (
                  <tr key={rate.id}>
                    <td><strong>{rate.installments}x</strong></td>
                    <td>{Number(rate.rate).toFixed(2)}%</td>
                    <td>
                      <span className={`badge ${rate.isActive ? 'badge-completed' : 'badge-cancelled'}`}>
                        {rate.isActive ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        <button
                          onClick={() => handleToggleActive(rate)}
                          className="btn btn-secondary btn-sm"
                          title={rate.isActive ? 'Desativar' : 'Ativar'}
                        >
                          {rate.isActive ? <PowerOff size={14} /> : <Power size={14} />}
                        </button>
                        <button
                          onClick={() => handleEdit(rate)}
                          className="btn btn-secondary btn-sm"
                          title="Editar"
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          onClick={() => handleDelete(rate.id)}
                          className="btn btn-secondary btn-sm"
                          title="Excluir"
                          style={{ color: 'var(--color-danger)' }}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default InstallmentRates;
